import {FC, useContext} from "react";
import {Box, Typography, Button, Paper, CircularProgress} from "@mui/material";
import {CheckCircle as CheckCircleIcon, ArrowBack as ArrowBackIcon} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import {useGetCourseDetails} from "@/app2/shared/data";
import {LocaleContext, resolveLocale} from "@/app2/shared/contexts";

interface CourseCertificatePageProps {
	courseSlug: string;
}

/**
 * Course Certificate Page
 * 
 * Displays course completion certificate/congratulations.
 * Shows completion date and allows navigation back to course.
 */
export const CourseCertificatePage: FC<CourseCertificatePageProps> = ({courseSlug}) => {
	const navigate = useNavigate();
	const [{locale}] = useContext(LocaleContext);
	const detailsParams = {locale: resolveLocale(locale)};
	const {data: courseData, isLoading} = useGetCourseDetails(courseSlug, detailsParams);

	const course = courseData?.course;
	const enrollment = course?.enrollment;

	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
				<CircularProgress />
			</Box>
		);
	}

	if (!enrollment || !enrollment.completedAt) {
		return (
			<Box textAlign="center" py={8}>
				<Typography variant="h5" gutterBottom>
					Course Not Completed
				</Typography>
				<Typography variant="body1" color="text.secondary" paragraph>
					Complete all modules to view your certificate.
				</Typography>
				<Button
					variant="contained"
					onClick={() => navigate(`/course/${courseSlug}`)}
				>
					Back to Course
				</Button>
			</Box>
		);
	}

	const completedDate = new Date(enrollment.completedAt).toLocaleDateString("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric"
	});

	return (
		<Box py={4}>
			<Box mb={3}>
				<Button
					startIcon={<ArrowBackIcon />}
					onClick={() => navigate(`/course/${courseSlug}`)}
					size="small"
				>
					Back to Course
				</Button>
			</Box>

			<Paper
				elevation={3}
				sx={{
					p: 6,
					textAlign: "center",
					maxWidth: 800,
					mx: "auto"
				}}
			>
				<CheckCircleIcon
					sx={{
						fontSize: 80,
						color: "success.main",
						mb: 2
					}}
				/>

				<Typography variant="h3" gutterBottom>
					Congratulations!
				</Typography>

				<Typography variant="h5" color="text.secondary" paragraph>
					You have completed
				</Typography>

				<Typography variant="h4" gutterBottom sx={{fontWeight: 600}}>
					{course?.title}
				</Typography>

				<Box my={4}>
					<Typography variant="body1" color="text.secondary">
						Completed on {completedDate}
					</Typography>
				</Box>

				<Button
					variant="contained"
					size="large"
					onClick={() => navigate("/courses")}
					sx={{mt: 2}}
				>
					Explore More Courses
				</Button>
			</Paper>
		</Box>
	);
};

